import { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { TestEnvBanner } from "@/components/TestEnvBanner";
import { UpdatePrompt } from "@/components/UpdatePrompt";
import Login from "@/pages/Login";
import { SITE_HOST } from "@/lib/site";

// Route components are lazy-loaded so the login screen ships a tiny bundle (fast on mobile).
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Parts = lazy(() => import("@/pages/Parts"));
const QuoteBuilder = lazy(() => import("@/pages/QuoteBuilder"));
const SavedQuotes = lazy(() => import("@/pages/SavedQuotes"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const MyJobs = lazy(() => import("@/pages/MyJobs"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const JobCompletion = lazy(() => import("@/pages/JobCompletion"));
const CostVariance = lazy(() => import("@/pages/CostVariance"));
const Contracts = lazy(() => import("@/pages/Contracts"));
const Customers = lazy(() => import("@/pages/Customers"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const FeatureLibrary = lazy(() => import("@/pages/FeatureLibrary"));
const ServiceSchedule = lazy(() => import("@/pages/ServiceSchedule"));
const DaySchedule = lazy(() => import("@/pages/DaySchedule"));
const SimTopups = lazy(() => import("@/pages/SimTopups"));
const WorkOrders = lazy(() => import("@/pages/WorkOrders"));
const Addons = lazy(() => import("@/pages/Addons"));
const Certificates = lazy(() => import("@/pages/Certificates"));
const Settings = lazy(() => import("@/pages/Settings"));
const Dispatch = lazy(() => import("@/pages/Dispatch"));
const MySchedule = lazy(() => import("@/pages/MySchedule"));
const Monitoring = lazy(() => import("@/pages/Monitoring"));
const TimeKeeping = lazy(() => import("@/pages/TimeKeeping"));
const PublicQuote = lazy(() => import("@/pages/PublicQuote"));
const Enquiry = lazy(() => import("@/pages/Enquiry"));
const Landing = lazy(() => import("@/pages/Landing"));
const Website = lazy(() => import("@/pages/Website"));
const SocialCalendar = lazy(() => import("@/pages/SocialCalendar"));
const TeamMessages = lazy(() => import("@/pages/TeamMessages"));
const WhatsAppCampaigns = lazy(() => import("@/pages/WhatsAppCampaigns"));
const ProfitLoss = lazy(() => import("@/pages/ProfitLoss"));
const HubInvoices = lazy(() => import("@/pages/HubInvoices"));

function FullScreenLoader() {
  return (
    <div className="h-screen flex items-center justify-center bg-brand-navy text-brand-gold font-mono text-sm">
      Loading…
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

const CAMERAS_HOST = window.location.hostname.startsWith("cameras.");

const siteRoutes = (pre) => [
  <Route key="h" path={pre || "/"} element={<Website page="home" />} />,
  <Route key="s" path={`${pre}/services`} element={<Website page="services" />} />,
  <Route key="sd" path={`${pre}/services/:slug`} element={<Website page="service" />} />,
  <Route key="a" path={`${pre}/about`} element={<Website page="about" />} />,
  <Route key="r" path={`${pre}/reviews`} element={<Website page="reviews" />} />,
  <Route key="c" path={`${pre}/contact`} element={<Website page="contact" />} />,
  <Route key="p" path={`${pre}/privacy`} element={<Website page="privacy" />} />,
];

function WebsiteRoutes() {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        {siteRoutes("")}
        <Route path="/products" element={<Landing />} />
        <Route path="/products/:slug" element={<Landing />} />
        <Route path="/enquiry" element={<Enquiry />} />
        <Route path="/q/:token" element={<PublicQuote />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function CameraSiteRoutes() {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/products" element={<Landing />} />
        <Route path="/products/:slug" element={<Landing />} />
        <Route path="/enquiry" element={<Enquiry />} />
        <Route path="/q/:token" element={<PublicQuote />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function AppRoutes() {
  if (CAMERAS_HOST) return <CameraSiteRoutes />;
  if (SITE_HOST) return <WebsiteRoutes />;
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        {siteRoutes("/site")}
        <Route path="/q/:token" element={<PublicQuote />} />
        <Route path="/enquiry" element={<Enquiry />} />
        <Route path="/products" element={<Landing />} />
        <Route path="/products/:slug" element={<Landing />} />
        <Route path="/" element={<Protected><Dashboard /></Protected>} />
        <Route path="/suppliers" element={<Protected><Suppliers /></Protected>} />
        <Route path="/parts" element={<Protected><Parts /></Protected>} />
        <Route path="/quote/new" element={<Protected><QuoteBuilder /></Protected>} />
        <Route path="/quote/:id" element={<Protected><QuoteBuilder /></Protected>} />
        <Route path="/quotes" element={<Protected><SavedQuotes /></Protected>} />
        <Route path="/profit-loss" element={<Protected><ProfitLoss /></Protected>} />
        <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
        <Route path="/social-calendar" element={<Protected><SocialCalendar /></Protected>} />
        <Route path="/messages" element={<Protected><TeamMessages /></Protected>} />
        <Route path="/whatsapp" element={<Protected><WhatsAppCampaigns /></Protected>} />
        <Route path="/my-jobs" element={<Protected><MyJobs /></Protected>} />
        <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
        <Route path="/job-completion" element={<Protected><JobCompletion /></Protected>} />
        <Route path="/cost-variance" element={<Protected><CostVariance /></Protected>} />
        <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
        <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
        <Route path="/hub-invoices" element={<Protected><HubInvoices /></Protected>} />
        <Route path="/customers" element={<Protected><Customers /></Protected>} />
        <Route path="/features" element={<Protected><FeatureLibrary /></Protected>} />
        <Route path="/services" element={<Protected><ServiceSchedule /></Protected>} />
        <Route path="/day-schedule" element={<Protected><DaySchedule /></Protected>} />
        <Route path="/sim-topups" element={<Protected><SimTopups /></Protected>} />
        <Route path="/work-orders" element={<Protected><WorkOrders /></Protected>} />
        <Route path="/addons" element={<Protected><Addons /></Protected>} />
        <Route path="/certificates" element={<Protected><Certificates /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/dispatch" element={<Protected><Dispatch /></Protected>} />
        <Route path="/my-schedule" element={<Protected><MySchedule /></Protected>} />
        <Route path="/monitoring" element={<Protected><Monitoring /></Protected>} />
        <Route path="/timekeeping" element={<Protected><TimeKeeping /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <TestEnvBanner />
      <UpdatePrompt />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
